import { useIntl } from 'react-intl';
import { Tile } from '@rossum/components/tile';
import { OperationLog } from '@rossum/mdh-api-client';
import {
  IconAlertOctagon,
  IconCircleCheck,
  IconPointFilled,
} from '@rossum/ui/icons/tabler';
import {
  CircularProgress,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { datasetTablePath } from '../../routes';
import { Dataset } from '../../types';
import { getDatasetStatus } from '../filterDatasets';

export const DatasetTile = ({
  table,
  operations,
}: {
  table: Dataset;
  operations: OperationLog[] | undefined;
}) => {
  const intl = useIntl();

  const lastOperation = operations?.[0];
  const status = getDatasetStatus(operations);
  const uploadInProgress = status === 'inProgress';

  const inlineBadges =
    status === 'failed'
      ? [
          {
            Icon: IconAlertOctagon,
            key: 'lastUpdateFailed',
            label: intl.formatMessage({
              id: 'features.datasets.list.lastUpdatedFailed.new',
            }),
            color: 'error' as const,
            tooltip: operations?.[0]?.message ?? '',
          },
        ]
      : status === 'completed'
        ? [
            {
              Icon: IconCircleCheck,
              key: 'lastUpdateSuccess',
              label: intl.formatMessage({
                id: 'features.datasets.list.lastUpdatedSuccess',
              }),
              color: 'success' as const,
              tooltip: operations?.[0]?.message ?? '',
            },
          ]
        : undefined;

  const description = (
    <Stack spacing={0.5} direction="row" alignItems="center">
      <Typography variant="caption" color="text.secondary">
        {table.name}
      </Typography>
      {uploadInProgress || lastOperation?.updated ? (
        <SvgIcon fontSize="small" sx={{ color: 'text.disabled' }}>
          <IconPointFilled />
        </SvgIcon>
      ) : null}

      {uploadInProgress ? (
        <Stack direction="row" alignItems="center" gap={1}>
          <CircularProgress color="secondary" size={16} />
          <Typography variant="caption" color="text.secondary">
            {intl.formatMessage({
              id:
                lastOperation?.type === 'create'
                  ? 'features.datasets.list.uploadInProgress.creating'
                  : 'features.datasets.list.uploadInProgress.updating',
            })}
          </Typography>
        </Stack>
      ) : lastOperation?.updated ? (
        <Typography variant="caption" color="text.secondary">
          {intl.formatMessage({
            id: 'features.datasets.list.lastUpdated',
          })}
          :{' '}
          {intl.formatDate(new Date(lastOperation.updated), {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </Typography>
      ) : null}
    </Stack>
  );

  return (
    <Tile
      title={table.metadata.name}
      endChips={inlineBadges}
      description={description}
      to={datasetTablePath(table.name)}
      dragHandle={null}
      data-cy="dataset-tile"
    />
  );
};
